import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Drawer,
  DrawerHeader,
  DrawerBody,
  Text,
  Amount,
  Badge,
  Divider,
  Link,
  Spinner,
  PaperclipIcon,
  RadioGroup,
  Radio,
  Switch,
  TextInput,
  TextArea,
  Button,
  RupeeIcon,
  RefreshIcon,
} from '@razorpay/blade/components';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import { getFlexiReimbursementClaim } from 'api/gql-utils/flexibleBenefits/queries/getFlexiReiumbersmentClaim';
import { getClaimEvidenceFiles } from 'api/gql-utils/flexibleBenefits/queries/getClaimEvidenceFiles';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { DecisionType, FlexiClaimStatus } from './types';
import { getClaimStatusColor, getClaimStatusTitle } from './utils';
import { PDFViewer } from 'components/ui/PDFViewer';
import { generateFile } from 'components/SalaryComponents/views/FlexibleBenefits/Claims/utils';
import { convertRupeesToPaise } from 'components/SalaryComponents/views/FlexibleBenefits/Claims/Edit/util';
import { convertPaiseToRupees } from 'components/SalaryComponents/views/FlexibleBenefits/Claims/Edit/util';

type ClaimDetailDrawerProps = {
  isOpen: boolean;
  onDismiss: () => void;
  claimId: string | null;
  initialDecision?: DecisionType;
  onStatusChange?: (data: {
    claimId: string;
    approvedAmount?: number;
    review_comments: string;
    status: FlexiClaimStatus;
  }) => void;
  viewOnly?: boolean;
};

type DetailRowProps = {
  label: string;
  children: React.ReactNode;
};

const DetailRow = ({ label, children }: DetailRowProps) => (
  <Box display="flex" flexDirection="row" gap="spacing.5">
    <Box minWidth="140px">
      <Text size="medium" weight="regular" color="surface.text.gray.muted">
        {label}
      </Text>
    </Box>
    <Box flex="1">{children}</Box>
  </Box>
);

export const ClaimDetailDrawer = ({
  isOpen,
  onDismiss,
  claimId,
  initialDecision,
  onStatusChange,
  viewOnly = false,
}: ClaimDetailDrawerProps) => {
  const [decision, setDecision] = useState<DecisionType>(initialDecision ?? 'approve');
  const [approveFullAmount, setApproveFullAmount] = useState(true);
  const [approvedAmount, setApprovedAmount] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    data: claimData,
    isLoading: isClaimLoading,
    isError: isClaimError,
    refetch,
  } = useQuery({
    queryKey: ['getFlexiReimbursementClaim', claimId],
    queryFn: () => getFlexiReimbursementClaim({ claim_id: claimId! }),
    enabled: isOpen && !!claimId,
    select: (data) => data.payrollGetFlexiReimbursementClaim.claim,
    onSuccess: (data) => {
      setApprovedAmount(convertPaiseToRupees(data.claim_amount).toString());
    },
  });

  const { data: evidenceFiles, isLoading: isEvidenceLoading } = useQuery({
    queryKey: ['getClaimEvidenceFiles', claimData?.claim_evidence_id],
    queryFn: () =>
      getClaimEvidenceFiles({
        file_requests: claimData!.claim_evidence_id.map((id) => ({ file_id: id })),
      }),
    enabled: isOpen && !!claimData?.claim_evidence_id?.length,
    select: (data) =>
      data.payrollGetBatchSignedURLs.data.map((file) =>
        generateFile(
          file.size,
          `${file.name}.${file.extension}`,
          file.extension,
          file.signed_url,
          file.file_id,
        ),
      ),
  });

  const files = evidenceFiles;

  useEffect(() => {
    if (claimData) {
      setDecision(initialDecision ?? 'approve');
      setReviewComments('');
    }
  }, [claimData, initialDecision]);

  const isLoading = isClaimLoading;
  const isPendingClaim = claimData?.claim_status === 'SUBMITTED';
  const isProcessedClaim = claimData?.claim_status !== 'SUBMITTED';

  const getApprovalAmount = () => {
    return parseFloat(approvedAmount) || 0;
  };

  const handleSubmit = () => {
    if (!claimData) return;

    if (decision === 'approve') {
      onStatusChange?.({
        claimId: claimData.claim_id,
        status: 'APPROVED',
        approvedAmount: convertRupeesToPaise(getApprovalAmount().toString()),
        review_comments: reviewComments,
      });
    } else {
      onStatusChange?.({
        claimId: claimData.claim_id,
        status: 'REJECTED',
        review_comments: reviewComments,
      });
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderAttachments = () => {
    if (!claimData?.claim_evidence_id?.length) {
      return (
        <Text size="medium" weight="regular" color="surface.text.gray.subtle">
          No attachments
        </Text>
      );
    }

    const attachmentCount = claimData.claim_evidence_id.length;

    return (
      <Link
        iconPosition="left"
        icon={PaperclipIcon}
        onClick={() => {
          setIsPreviewOpen(true);
        }}>
        {attachmentCount}
        {attachmentCount === 1 ? ' attachment' : ' attachments'}
      </Link>
    );
  };

  const renderApprovalSection = () => {
    if (viewOnly || !isPendingClaim || !claimData) return null;

    return (
      <>
        <Box display="flex" flexDirection="column" gap="spacing.6">
          <RadioGroup
            label=""
            name="claim-decision"
            value={decision}
            onChange={({ value }) => setDecision(value as DecisionType)}>
            <Box display="flex" flexDirection="row" gap="spacing.6">
              <Radio value="approve">Accept claim</Radio>
              <Radio value="reject">Reject claim</Radio>
            </Box>
          </RadioGroup>

          {decision === 'approve' && (
            <Box display="flex" flexDirection="column" gap="spacing.5">
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center">
                <Text size="medium" weight="regular" color="surface.text.gray.normal">
                  Approve full amount
                </Text>
                <Switch
                  accessibilityLabel="Approve full amount toggle"
                  isChecked={approveFullAmount}
                  onChange={({ isChecked }) => {
                    setApproveFullAmount(isChecked);

                    if (isChecked) {
                      setApprovedAmount(convertPaiseToRupees(claimData.claim_amount).toString());
                    }
                  }}
                />
              </Box>

              <TextInput
                label="Approved Amount"
                placeholder="Enter amount"
                name="approvedAmount"
                value={approvedAmount}
                onChange={({ value }) => setApprovedAmount(value ?? '')}
                isDisabled={approveFullAmount}
                leadingIcon={RupeeIcon}
                type="number"
              />
            </Box>
          )}

          <TextArea
            label="Additional comment"
            placeholder="Add a comment (optional)"
            name="comment"
            value={reviewComments}
            onChange={({ value }) => setReviewComments(value ?? '')}
            numberOfLines={2}
          />

          <Button isFullWidth onClick={handleSubmit}>
            {decision === 'approve'
              ? `Approve (${formatAmount(getApprovalAmount())})`
              : 'Reject claim'}
          </Button>
        </Box>

        <Divider marginY="spacing.4" />
      </>
    );
  };

  const renderProcessedClaimSection = () => {
    if (!viewOnly || !isProcessedClaim || !claimData) return null;

    const reviewCommentText = claimData.claim_comments || '-';
    const isApprovedClaim =
      claimData.claim_status === 'APPROVED' || claimData.claim_status === 'PARTIALLY_APPROVED';

    return (
      <>
        <Box display="flex" flexDirection="column" gap="spacing.5">
          <DetailRow label="Status">
            <Badge color={getClaimStatusColor(claimData.claim_status as FlexiClaimStatus)}>
              {getClaimStatusTitle(claimData.claim_status as FlexiClaimStatus)}
            </Badge>
          </DetailRow>

          {claimData.claim_status === 'REJECTED' && (
            <>
              <DetailRow label="Rejected by">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {claimData.approver_user_name || '-'}
                </Text>
              </DetailRow>

              <DetailRow label="Description">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {reviewCommentText}
                </Text>
              </DetailRow>

              <DetailRow label="Rejected date">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {claimData.updated_at
                    ? getFormattedDateValue({
                        date: new Date(claimData.updated_at * 1000),
                        formatString: dateFormats.dateShortMonthCommaYear,
                      })
                    : '-'}
                </Text>
              </DetailRow>
            </>
          )}

          {isApprovedClaim && (
            <>
              <DetailRow label="Approved by">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {claimData.approver_user_name || '-'}
                </Text>
              </DetailRow>

              <DetailRow label="Description">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {reviewCommentText}
                </Text>
              </DetailRow>

              <DetailRow label="Approved date">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  {claimData.updated_at
                    ? getFormattedDateValue({
                        date: new Date(claimData.updated_at * 1000),
                        formatString: dateFormats.dateShortMonthCommaYear,
                      })
                    : '-'}
                </Text>
              </DetailRow>
              <DetailRow label="Approved amount">
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  <Amount
                    value={Number(convertPaiseToRupees(claimData.approved_amount))}
                    color="surface.text.gray.subtle"
                  />
                </Text>
              </DetailRow>
            </>
          )}
        </Box>

        <Divider marginY="spacing.5" />
      </>
    );
  };

  const renderDetails = () => {
    if (!claimData) return null;

    const requestedDate = getFormattedDateValue({
      date: new Date(claimData.created_at * 1000),
      formatString: dateFormats.dateShortMonthCommaYear,
    });

    const expenseDate = getFormattedDateValue({
      date: new Date(Number(claimData.expense_date) * 1000),
      formatString: dateFormats.dateShortMonthCommaYear,
    });

    return (
      <Box display="flex" flexDirection="column" gap="spacing.5">
        {(isPendingClaim || viewOnly) && (
          <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
            Flexi Reimbursement Details
          </Text>
        )}

        <DetailRow label="Requested date">
          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
            {requestedDate}
          </Text>
        </DetailRow>

        <DetailRow label="Expense date">
          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
            {expenseDate}
          </Text>
        </DetailRow>

        <DetailRow label="Benefit type">
          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
            {claimData.component_display_name}
          </Text>
        </DetailRow>

        <DetailRow label="Amount">
          <Amount
            value={Number(convertPaiseToRupees(claimData.claim_amount))}
            color="surface.text.gray.subtle"
          />
        </DetailRow>

        <DetailRow label="Proof uploaded">
          {isEvidenceLoading ? (
            <Spinner accessibilityLabel="Loading attachments" />
          ) : (
            renderAttachments()
          )}
        </DetailRow>

        <DetailRow label="Description">
          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
            {claimData.claim_description || '-'}
          </Text>
        </DetailRow>
      </Box>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" height="200px">
          <Spinner accessibilityLabel="Loading claim details" />
        </Box>
      );
    }

    if (isClaimError) {
      return (
        <EmptyView
          description={
            <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
              Due to an unknown error, we could not load the claim details.
            </Text>
          }>
          <Box display="flex" justifyContent="center">
            <Box width="104px">
              <Button icon={RefreshIcon} size="small" onClick={() => refetch()}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyView>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap="spacing.5">
        {renderProcessedClaimSection()}
        {renderApprovalSection()}
        {renderDetails()}
      </Box>
    );
  };

  const getDrawerTitle = () => {
    if (viewOnly && claimData?.component_display_name) {
      return `${claimData.component_display_name} Claim`;
    }
    return 'Flexi reimbursement claim';
  };

  const getDrawerSubtitle = () => {
    if (!claimData) return undefined;

    if (viewOnly) {
      return `Raised By ${claimData.employee_name || `Employee ${claimData.employee_id}`}`;
    }

    return `Raised by Employee ${claimData.employee_id}`;
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onDismiss={onDismiss}
        accessibilityLabel="Flexi reimbursement claim details">
        <DrawerHeader title={getDrawerTitle()} subtitle={getDrawerSubtitle()} />
        <DrawerBody>{renderContent()}</DrawerBody>
      </Drawer>
      <PDFViewer
        isOpen={isPreviewOpen}
        files={files || []}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
};
